import React, { useState, useEffect } from 'react';
import './App.css';
import Footer from './Footer';
import { trackPurchase } from './GoogleAnalytics';
import { trackConversion } from './ConversionTracking';

export default function SuccessPage() {
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      fetchSessionData(sessionId);
      
      // Clear cart and checkout flags after successful payment
      localStorage.removeItem('presspeels_cart_metadata');
      localStorage.removeItem('presspeels_checkout_initiated');
      localStorage.removeItem('presspeels_checkout_timestamp');
      
      // Clear IndexedDB cart files
      const clearIndexedDBFiles = async () => {
        try {
          const request = indexedDB.open('PresspeelsCartDB', 1);
          request.onsuccess = () => {
            const db = request.result;
            if (db.objectStoreNames.contains('cartFiles')) {
              const transaction = db.transaction(['cartFiles'], 'readwrite');
              const store = transaction.objectStore('cartFiles');
              store.clear();
              console.log('🧹 IndexedDB cart files cleared after successful payment');
            }
          };
        } catch (error) {
          console.error('Error clearing IndexedDB files:', error);
        }
      };
      clearIndexedDBFiles();
    } else {
      setError('No session ID found');
      setLoading(false);
    }
  }, []);

  const fetchSessionData = async (sessionId) => {
    try {
      // REACT_APP_API_BASE_URL is baked in at build time by Vercel.
      // Set it per environment (Production = 4534, Preview/Staging = 5d0c9).
      // Fallback here is local dev only.
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4001/api';
      const response = await fetch(`${API_BASE_URL}/session/${sessionId}`);
      const data = await response.json();
      
      if (data.success) {
        setSessionData(data.session);
        
        // Track purchase in Google Analytics
        if (data.session) {
          const sessionData = data.session;
          const transactionId = sessionData.id || sessionId;
          const value = sessionData.amount_total ? sessionData.amount_total / 100 : 0; // Convert from cents
          const currency = sessionData.currency || 'USD';
          
          // Extract items from line_items if available
          const items = sessionData.line_items?.data?.map(item => ({
            id: item.price?.product || item.id,
            name: item.description || 'Custom Print',
            category: 'Custom Prints',
            quantity: item.quantity || 1,
            price: item.amount_total ? item.amount_total / 100 : 0,
          })) || [];
          
          trackPurchase(transactionId, value, currency.toUpperCase(), items);

          // Fire ad-platform conversions (Google Ads / Meta Pixel). No-op until IDs are set.
          trackConversion({
            transactionId,
            value,
            currency: currency.toUpperCase(),
            items,
          });
        }
      } else {
        setError('Failed to retrieve session data');
      }
    } catch (err) {
      setError('Failed to fetch session data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #fafafa 0%, #e5e5e5 100%)',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', color: '#666' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #fafafa 0%, #e5e5e5 100%)',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '3rem',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
          <h1 style={{ color: '#dc2626', marginBottom: '1rem' }}>Error</h1>
          <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              background: '#10b981',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    }}>
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '1rem' : '2rem'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: isMobile ? '2rem 1.5rem' : '3rem 2.5rem',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          maxWidth: '500px',
          width: '100%',
          border: '1px solid #e2e8f0'
        }}>
          {/* Success Icon */}
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem auto'
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
              <path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"/>
            </svg>
          </div>
          
          <h1 style={{ 
            color: '#111827', 
            fontSize: isMobile ? '1.8rem' : '2.2rem', 
            fontWeight: '700',
            marginBottom: '0.5rem',
            letterSpacing: '-0.025em',
            lineHeight: '1.2'
          }}>
            Payment Successful!
          </h1>
          
          <p style={{ 
            color: '#6b7280', 
            fontSize: isMobile ? '0.95rem' : '1rem',
            marginBottom: '2rem',
            lineHeight: '1.6'
          }}>
            Thank you for your order! Your custom DTF print is being processed.
          </p>

          {sessionData && (
            <div style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem',
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{ 
                margin: '0 0 1rem 0', 
                color: '#374151',
                fontSize: '1.1rem',
                fontWeight: '600',
                textAlign: 'center'
              }}>
                Order Summary
              </h3>
              
              <div style={{ display: 'grid', gap: '0.75rem', fontSize: '0.95rem' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.5rem 0',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <span style={{ color: '#6b7280', fontWeight: '500' }}>Amount:</span>
                  <span style={{ 
                    color: '#111827', 
                    fontWeight: '700',
                    fontSize: '1.1rem'
                  }}>
                    ${(sessionData.amount_total / 100).toFixed(2)} {sessionData.currency.toUpperCase()}
                  </span>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.5rem 0'
                }}>
                  <span style={{ color: '#6b7280', fontWeight: '500' }}>Status:</span>
                  <span style={{ 
                    background: sessionData.payment_status === 'paid' ? '#dcfdf7' : '#fef3c7',
                    color: sessionData.payment_status === 'paid' ? '#047857' : '#92400e',
                    fontWeight: '600',
                    textTransform: 'capitalize',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.85rem'
                  }}>
                    {sessionData.payment_status}
                  </span>
                </div>

                {sessionData.customer_details?.email && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem 0'
                  }}>
                    <span style={{ color: '#6b7280', fontWeight: '500', flexShrink: 0 }}>Email:</span>
                    <span style={{
                      color: '#111827',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      minWidth: 0,
                      overflowWrap: 'anywhere',
                      textAlign: 'right'
                    }}>
                      {sessionData.customer_details.email}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Info Box */}
          <div style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            textAlign: 'left'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#3b82f6" style={{ marginTop: '2px', flexShrink: 0 }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <div>
                <p style={{ 
                  color: '#1e40af', 
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  margin: '0 0 0.25rem 0'
                }}>
                  Order Confirmation
                </p>
                <p style={{ 
                  color: '#1e40af', 
                  fontSize: '0.85rem',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  You will receive an email confirmation shortly with your order details and tracking information.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => window.location.href = '/'}
            style={{
              background: 'linear-gradient(135deg, #635bff 0%, #5a52e8 100%)',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 15px rgba(99, 91, 255, 0.3)',
              width: '100%'
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #5a52e8 0%, #4c46d6 100%)';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 20px rgba(99, 91, 255, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #635bff 0%, #5a52e8 100%)';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(99, 91, 255, 0.3)';
            }}
          >
            Order Another Print
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
} 